import type {
  CreateMarketEventDTO,
  CreateMarketEventExpenseDTO,
  CreateMarketEventItemDTO,
  IssueEventStockBatchDTO,
  MarketEventListQueryDTO,
  RecordMarketEventSaleDTO,
  StockQuantityDTO,
  UpdateMarketEventDTO,
  UpdateMarketEventExpenseDTO,
  UpdateMarketEventItemDTO,
} from "@/modules/event-sales/domain/contracts";
import type {
  MarketEventDetailDTO,
  MarketEventDTO,
  MarketEventExpenseDTO,
  MarketEventItemDTO,
  MarketEventItemSuggestionDTO,
  MarketEventListItemDTO,
  MarketEventSaleDTO,
  MarketEventStockIssueDTO,
  MarketEventSummaryDTO,
  StockVariantSuggestionDTO,
} from "@/modules/event-sales/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

const base = () => `${config.apiUrl}/event-sales`;

export const eventSalesApi = {
  async list(
    query?: MarketEventListQueryDTO,
  ): Promise<{ data: MarketEventListItemDTO[] }> {
    const url = new URL(base());
    if (query?.status) url.searchParams.set("status", query.status);
    if (query?.search) url.searchParams.set("search", query.search);
    if (query?.limit) url.searchParams.set("limit", String(query.limit));
    return fetcher.get(url.toString());
  },
  async get(id: string): Promise<MarketEventDetailDTO> {
    return fetcher.get(`${base()}/${id}`);
  },
  async create(input: CreateMarketEventDTO): Promise<MarketEventDTO> {
    return fetcher.post(base(), input);
  },
  async update(
    id: string,
    input: UpdateMarketEventDTO,
  ): Promise<MarketEventDTO> {
    return fetcher.patch(`${base()}/${id}`, input);
  },
  async open(id: string): Promise<MarketEventDTO> {
    return fetcher.post(`${base()}/${id}/open`, {});
  },
  async close(id: string): Promise<MarketEventDTO> {
    return fetcher.post(`${base()}/${id}/close`, {});
  },
  async remove(id: string): Promise<{ id: string }> {
    return fetcher.delete(`${base()}/${id}`);
  },
  async items(id: string): Promise<{ data: MarketEventItemDTO[] }> {
    return fetcher.get(`${base()}/${id}/items`);
  },
  async createItem(
    id: string,
    input: CreateMarketEventItemDTO,
  ): Promise<MarketEventItemDTO> {
    return fetcher.post(`${base()}/${id}/items`, input);
  },
  async updateItem(
    id: string,
    itemId: string,
    input: UpdateMarketEventItemDTO,
  ): Promise<MarketEventItemDTO> {
    return fetcher.patch(`${base()}/${id}/items/${itemId}`, input);
  },
  async deleteItem(id: string, itemId: string): Promise<{ id: string }> {
    return fetcher.delete(`${base()}/${id}/items/${itemId}`);
  },
  async suggest(
    eventId: string,
    q: string,
  ): Promise<{ data: MarketEventItemSuggestionDTO[] }> {
    const url = new URL(`${base()}/items/suggest`);
    url.searchParams.set("eventId", eventId);
    if (q) url.searchParams.set("q", q);
    return fetcher.get(url.toString());
  },
  async sales(id: string): Promise<{ data: MarketEventSaleDTO[] }> {
    return fetcher.get(`${base()}/${id}/sales`);
  },
  async recordSale(
    id: string,
    input: RecordMarketEventSaleDTO,
  ): Promise<MarketEventSaleDTO> {
    return fetcher.post(`${base()}/${id}/sales`, input);
  },
  async deleteSale(id: string, saleId: string): Promise<{ id: string }> {
    return fetcher.delete(`${base()}/${id}/sales/${saleId}`);
  },
  async expenses(id: string): Promise<{ data: MarketEventExpenseDTO[] }> {
    return fetcher.get(`${base()}/${id}/expenses`);
  },
  async createExpense(
    id: string,
    input: CreateMarketEventExpenseDTO,
  ): Promise<MarketEventExpenseDTO> {
    return fetcher.post(`${base()}/${id}/expenses`, input);
  },
  async updateExpense(
    id: string,
    expenseId: string,
    input: UpdateMarketEventExpenseDTO,
  ): Promise<MarketEventExpenseDTO> {
    return fetcher.patch(`${base()}/${id}/expenses/${expenseId}`, input);
  },
  async deleteExpense(id: string, expenseId: string): Promise<{ id: string }> {
    return fetcher.delete(`${base()}/${id}/expenses/${expenseId}`);
  },
  async summary(id: string): Promise<MarketEventSummaryDTO> {
    return fetcher.get(`${base()}/${id}/summary`);
  },
  async stockIssues(id: string): Promise<{ data: MarketEventStockIssueDTO[] }> {
    return fetcher.get(`${base()}/${id}/stock`);
  },
  async suggestStockVariants(
    q: string,
  ): Promise<{ data: StockVariantSuggestionDTO[] }> {
    const url = new URL(`${base()}/stock/variants`);
    if (q) url.searchParams.set("q", q);
    return fetcher.get(url.toString());
  },
  async issueStock(
    id: string,
    input: IssueEventStockBatchDTO,
  ): Promise<MarketEventStockIssueDTO[]> {
    return fetcher.post(`${base()}/${id}/stock`, input);
  },
  async returnStock(
    id: string,
    issueId: string,
    input: StockQuantityDTO,
  ): Promise<{ id: string }> {
    return fetcher.post(`${base()}/${id}/stock/${issueId}/return`, input);
  },
  async consumeStock(
    id: string,
    issueId: string,
    input: StockQuantityDTO,
  ): Promise<{ id: string }> {
    return fetcher.post(`${base()}/${id}/stock/${issueId}/use`, input);
  },
};
